import axios from 'axios';
import { getApiBaseUrl } from '../../config/api';
import { dedupeSubjectsById } from '../../utils/dedupeSubjects';
import { enrichSubjectsWithClassTeachers } from '../../utils/subjectTeacherMap';
import { getSchoolIdForUser } from '../../utils/userContext';
import {
    getRequest,
    getSuccess,
    getFailed,
    getError,
    getStudentsSuccess,
    detailsSuccess,
    getFailedTwo,
    getSubjectsSuccess,
    getSubDetailsSuccess,
    getSubDetailsRequest
} from './sclassSlice';
import { authFailed, authRequest, stuffAdded } from '../userRelated/userSlice';

export const getAllSclasses = (id, address = "Sclass") => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/${address}List/${id}`);
        if (result.data.message) {
            dispatch(getFailedTwo(result.data.message));
        } else {
            dispatch(getSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
}

export const getClassStudents = (id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/Sclass/Students/${id}`);
        if (result.data.message) {
            dispatch(getFailedTwo(result.data.message));
        } else {
            dispatch(getStudentsSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
}

export const getClassDetails = (id, address) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/${address}/${id}`);
        const data = result.data;
        const hasClass = Boolean(data?.id || data?._id);
        if (data?.message && !hasClass) {
            dispatch(getFailedTwo(data.message));
            return { success: false, message: data.message };
        }
        if (hasClass) {
            dispatch(detailsSuccess(data));
            return { success: true, data };
        }
        dispatch(getFailedTwo("Class not found"));
        return { success: false, message: "Class not found" };
    } catch (error) {
        dispatch(getError(error));
        return { success: false, error };
    }
}

export const updateSclass = (id, fields) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/Sclass/${id}`, fields);
        if (result.data?.message && !result.data?.id && !result.data?._id) {
            dispatch(getFailed(result.data.message));
            return { success: false, message: result.data.message };
        }
        dispatch(detailsSuccess(result.data));
        return { success: true, data: result.data };
    } catch (error) {
        dispatch(getError(error));
        const msg = error.response?.data?.message || error.message || "Could not update class";
        return { success: false, message: msg };
    }
};

export const getSubjectList = (id, address) => async (dispatch, getState) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/${address}/${id}`);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            let rows = Array.isArray(result.data) ? dedupeSubjectsById(result.data) : result.data;
            if (Array.isArray(rows) && address === "ClassSubjects") {
                const schoolId = getSchoolIdForUser(getState()?.user?.currentUser);
                if (schoolId) {
                    try {
                        const teachRes = await axios.get(`${getApiBaseUrl()}/Teachers/${schoolId}`);
                        const teachers = Array.isArray(teachRes.data) ? teachRes.data : [];
                        rows = enrichSubjectsWithClassTeachers(rows, teachers, id);
                    } catch {
                        /* ClassSubjects still usable without teacher names */
                    }
                }
            }
            dispatch(getSubjectsSuccess(rows));
        }
    } catch (error) {
        dispatch(getError(error));
    }
}

export const getTeacherFreeClassSubjects = (id, teacherId) => async (dispatch, getState) => {
    dispatch(getRequest());

    try {
        const query = teacherId ? `?teacherId=${encodeURIComponent(teacherId)}` : "";
        const result = await axios.get(`${getApiBaseUrl()}/FreeSubjectList/${id}${query}`);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            let rows = Array.isArray(result.data)
                ? dedupeSubjectsById(result.data)
                : result.data;
            if (Array.isArray(rows)) {
                const schoolId = getSchoolIdForUser(getState()?.user?.currentUser);
                if (schoolId) {
                    try {
                        const teachRes = await axios.get(`${getApiBaseUrl()}/Teachers/${schoolId}`);
                        const teachers = Array.isArray(teachRes.data) ? teachRes.data : [];
                        rows = enrichSubjectsWithClassTeachers(rows, teachers, id);
                    } catch {
                        /* ignore */
                    }
                }
            }
            dispatch(getSubjectsSuccess(rows));
        }
    } catch (error) {
        dispatch(getError(error));
    }
}

export const getSubjectDetails = (id, address) => async (dispatch) => {
    dispatch(getSubDetailsRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/${address}/${id}`);
        if (result.data) {
            dispatch(getSubDetailsSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error));
    }
}

export const createClassWithLevel = (fields) => async (dispatch) => {
    dispatch(authRequest());
    const baseUrl = getApiBaseUrl();

    try {
        const result = await axios.post(`${baseUrl}/ClassWithLevel`, fields, {
            headers: { 'Content-Type': 'application/json' },
        });
        if (result.data?.message && !result.data?.id && !result.data?._id) {
            dispatch(authFailed(result.data.message));
        } else {
            dispatch(stuffAdded(result.data));
        }
    } catch (error) {
        const data = error.response?.data;
        const msg =
            (typeof data === "object" && data?.message) ||
            (typeof data === "string" && data) ||
            error.message ||
            "Request failed";
        dispatch(authFailed(msg));
    }
};

export const bulkCreateClasses = (payload) => async (dispatch) => {
    dispatch(authRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/BulkCreateClasses`, payload, {
            headers: { "Content-Type": "application/json" },
        });
        const data = result.data;
        if (data?.message && data?.created === undefined && !Array.isArray(data?.classes)) {
            dispatch(authFailed(data.message));
            return { success: false, message: data.message };
        }
        dispatch(
            stuffAdded({
                bulk: true,
                created: data.created ?? 0,
                skipped: data.skipped ?? 0,
            })
        );
        return { success: true, ...data };
    } catch (error) {
        const msg = error.response?.data?.message || error.message || "Request failed";
        dispatch(authFailed(msg));
        return { success: false, message: msg };
    }
};

export const getLevelCodes = () => async (dispatch) => {
    try {
        const result = await axios.get(`${getApiBaseUrl()}/LevelCodes`);
        return result.data;
    } catch (error) {
        dispatch(getError(error));
        return null;
    }
};